import { Component, type ReactNode } from 'react';

/**
 * Tras un despliegue, una pestaña abierta con la versión anterior pide al
 * navegar trozos de JS (páginas lazy) que ya no existen y la carga falla. Sin
 * este límite React desmontaba todo y la pantalla quedaba en blanco
 * (/ticnova/importaciones y /ticnova/reposiciones, 23/10/2026).
 *
 * Si el error es de carga de un trozo, recarga la página una vez (con la
 * versión nueva). Cualquier otro error enseña un aviso con «Recargar».
 */

const CLAVE = 'pm-recarga-por-version';
const ERROR_DE_CARGA = /dynamically imported module|Importing a module script failed|error loading dynamically imported module|Failed to load module script|ChunkLoadError/i;

export function esErrorDeCarga(err: unknown): boolean {
  const msg = err instanceof Error ? `${err.name} ${err.message}` : String(err ?? '');
  return ERROR_DE_CARGA.test(msg);
}

/** Recarga una sola vez por minuto, para no entrar en bucle si el fallo persiste. */
export function recargarPorVersionNueva(): boolean {
  try {
    const ultima = Number(sessionStorage.getItem(CLAVE) ?? 0);
    if (Date.now() - ultima < 60_000) return false;
    sessionStorage.setItem(CLAVE, String(Date.now()));
  } catch { /* sin sessionStorage: recargar igualmente */ }
  window.location.reload();
  return true;
}

/** Vite avisa con este evento cuando falla la precarga de un import dinámico. */
export function instalarRecargaPorVersion() {
  window.addEventListener('vite:preloadError', (ev) => {
    if (recargarPorVersionNueva()) ev.preventDefault();
  });
}

type Props = { children: ReactNode };
type State = { error: unknown };

export class ErrorPagina extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: unknown): State {
    return { error };
  }

  componentDidCatch(error: unknown) {
    if (esErrorDeCarga(error)) recargarPorVersionNueva();
    console.error('[ErrorPagina]', error);
  }

  render() {
    if (this.state.error == null) return this.props.children;
    const deCarga = esErrorDeCarga(this.state.error);
    return (
      <div className="mx-auto mt-16 max-w-lg rounded-lg border border-slate-200 bg-white p-6 text-sm shadow-sm">
        <p className="font-semibold text-slate-800">
          {deCarga ? 'Hay una versión nueva de la aplicación.' : 'No se ha podido mostrar esta pantalla.'}
        </p>
        <p className="mt-1 text-slate-600">
          {deCarga ? 'Recarga la página para cargarla.' : 'Recarga la página. Si vuelve a pasar, avisa con la dirección de la pantalla.'}
        </p>
        {!deCarga && <pre className="mt-3 max-h-32 overflow-auto rounded bg-slate-50 p-2 text-[11px] text-slate-500">{String((this.state.error as Error)?.message ?? this.state.error)}</pre>}
        <button type="button" className="mt-4 rounded-md bg-slate-800 px-3 py-1.5 text-white hover:bg-slate-700" onClick={() => window.location.reload()}>Recargar</button>
      </div>
    );
  }
}
